<template>
  <div class="view-accounting">
    <h3 class="accounting-title" data-cy="accounting-title">{{ $t('viewAccounting.title') }} {{ totalRows }}</h3>
    <div>
      <div v-if="!loadingAccountBalance">
        <account-balance :selected-documents-total="selectedDocumentsTotal" />
      </div>
    </div>
    <div class="account-grid-cnt">
      <div class="buttons">
        <BackstageToggleButton
          v-if="!loading"
          toggle-id="accounting-documents"
          translationNamespace="viewAccounting.viewOptions"
          custom-width="150px"
          :viewOptions="[AccountingDocumentsViewOption.Open, AccountingDocumentsViewOption.Closed]"
          :defaultViewOption="AccountingDocumentsViewOption.Open"
          @view-option-updated="handleAccountingDocumentViewToggle"
          data-cy="accounting-documents-view-options" />
        <div class="action-buttons">
          <BackstageButton
            v-if="isPayButtonVisible"
            :disabled="isPayButtonDisabled"
            class="pay-button"
            data-cy="pay-button-cnt"
            data-cy-button="pay-button"
            v-on:click="goToCheckout()">
            <span>{{ $t('viewAccounting.pay_button') }}</span>
            <span v-if="selectedDocumentsTotal">: {{ selectedDocumentsTotal }} {{ accountDocumentCurrency }}</span>
          </BackstageButton>
          <BackstageButton
            icon-left="trash"
            :variant="ButtonVariant.SECONDARY"
            data-cy="clear-all-filters-button-cnt"
            data-cy-button="clear-all-filters-button"
            v-on:click="removeAllGridFilters()">
            {{ $t('global.grids.clear_all_filters_button') }}
          </BackstageButton>
        </div>
      </div>
      <div class="accounting-grid" data-cy="accounting-grid">
        <div v-if="loading" class="loader"></div>
        <div v-else>
          <div v-if="rowData.length > 0">
            <ag-grid-vue
              class="ag-theme-balham"
              :grid-options="gridOptions"
              :column-defs="columnDefinitions"
              :default-col-def="defaultColDef"
              :row-data="rowData"
              :modules="modules"
              :pagination="enablePagination"
              :pagination-page-size="pageSize"
              :suppress-menu-hide="true"
              :framework-components="frameworkComponents"
              :row-selection="'multiple'"
              :rowMultiSelectWithClick="true"
              :suppressRowClickSelection="!canSelectAll"
              v-on:grid-ready="onGridReady"
              v-on:first-data-rendered="onFirstDataRendered"
              v-on:selection-changed="onSelectionChanged">
            </ag-grid-vue>
          </div>
          <div v-else class="no-rows">
            {{ $t('viewAccounting.no_accounting_documents_message') }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="show3DSProcessing">
      <NotificationMessage :title="$t('viewAccounting.checking_3ds_result')" :size="'medium'">
        <div>
          <div class="loader"></div>
        </div>
      </NotificationMessage>
    </div>
  </div>
</template>

<script lang="ts">
import AccountBalance from '@/components/accounting/AccountBalance.vue';
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageToggleButton from '@/components/common/BackstageToggleButton.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import InvoiceStatusRenderer from '@/components/invoice/InvoiceStatusRenderer.vue';
import { useDocumentHelpers } from '@/composables/document-helpers';
import { useLocale } from '@/composables/locale';
import { DOCUMENT_SELECTION_LIMIT } from '@/constants/accounting.constants';
import { accountMixins } from '@/mixins/account.mixins';
import { ordersMixins } from '@/mixins/orders.mixins';
import { paymentsMixins } from '@/mixins/payments.mixins';
import { utilsMixins } from '@/mixins/utils.mixins';
import { AccountingDocument, AccountingDocumentStatus } from '@/models/accounting-document.model';
import { AccountingDocumentsViewOption } from '@/models/accounting-documents-view-options.enum.ts';
import { ButtonVariant } from '@/models/button-variant.enum';
import { GridConfig } from '@/models/grid-config';
import { GridNames } from '@/models/grids-names.enum';
import { RouteNames } from '@/router';
import { AccountingDocumentsService } from '@/services/accounting-documents.service';
import { OrderDocumentService } from '@/services/order-document-service';
import { PaymentsService } from '@/services/payments.service';
import { useAccountBalanceStore } from '@/stores/account-balance';
import { useAccountingDocumentsStore } from '@/stores/accounting-documents';
import { usePaymentStore } from '@/stores/payment';
import { useUserStore } from '@/stores/user';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import {
  GridApi,
  type CellClickedEvent,
  type CheckboxSelectionCallbackParams,
  type ColDef,
  type GridOptions,
} from '@ag-grid-community/core';
import { AgGridVue } from '@ag-grid-community/vue3';
import { AccountModel } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent, toRefs } from 'vue';
import { useFormat } from '../composables/format';
import { useGrid } from '../composables/grid';
import { useGridsStore } from '../stores/grids';

export default defineComponent({
  name: 'accounting-documents-grid',
  components: {
    BackstageButton,
    AgGridVue,
    AccountBalance,
    NotificationMessage,
    // eslint-disable-next-line vue/no-unused-components
    InvoiceStatusRenderer,
    BackstageToggleButton,
  },
  props: {
    enablePagination: {
      type: Boolean,
      default: true,
    },
    redirectResult: {
      type: String,
      default: undefined,
    },
    orderRef: {
      type: String,
      default: undefined,
    },
  },
  setup() {
    const { selectedAccountId, selectedAccount } = storeToRefs(useUserStore());
    const { userLocale } = useLocale();
    const userStore = useUserStore();
    const { addEditGridConfig, getGridByName } = useGridsStore();
    const { openPDF } = useDocumentHelpers();
    const { formatCurrency } = useFormat();
    const { defaultGridOptions, dateColumnDefinition, applyGridConfig } = useGrid();

    const accountingDocumentsStore = useAccountingDocumentsStore();
    const { openDocuments, closedDocuments } = toRefs(accountingDocumentsStore);

    const { accountBalance, loadingAccountBalance, isCreditCardPaymentEnabled, isCreditCardPaymentExceeded } =
      storeToRefs(useAccountBalanceStore());
    const { getAccountBalance } = useAccountBalanceStore();
    const { paymentData } = usePaymentStore();

    return {
      accountId: selectedAccountId,
      userLocale,
      selectedAccount,
      userStore,
      ButtonVariant,
      addEditGridConfig,
      getGridByName,
      openPDF,
      formatCurrency,
      defaultGridOptions,
      dateColumnDefinition,
      applyGridConfig,
      openDocuments,
      closedDocuments,
      setAccountingDocuments: accountingDocumentsStore.setAccountingDocuments,
      resetAccountingDocumentsStore: accountingDocumentsStore.$reset,
      AccountingDocumentsViewOption,
      getAccountBalance,
      isCreditCardPaymentEnabled,
      isCreditCardPaymentExceeded,
      loadingAccountBalance,
      accountBalance,
      paymentData,
    };
  },
  data() {
    return {
      loading: true,
      selectedAccountingDocumentType: AccountingDocumentsViewOption.Open,
      accountingDocumentsService: new AccountingDocumentsService(),
      accountDocumentCurrency: '',
      orderDocumentsService: new OrderDocumentService(),
      show3DSProcessing: false,
      paymentsService: new PaymentsService(),
      selectedDocumentIds: [] as number[],
      selectedDocumentsTotal: 0,
      columnDefinitions: new Array<ColDef>(),
      defaultColDef: {
        resizable: true,
        filter: true,
        sortable: true,
        minWidth: 80,
      },
      gridOptions: {
        ...this.defaultGridOptions(),
        domLayout: 'autoHeight',
        enableCellTextSelection: true,
        rowHeight: 40,
        suppressPaginationPanel: this.enablePagination === false,
      } as GridOptions<any> as any,
      totalRows: '',
      pageSize: 50,
      gridApi: undefined as GridApi | undefined,
      rowData: new Array<any>(),
      modules: [ClientSideRowModelModule],
      frameworkComponents: {},
      gridName: GridNames.AccountingDocuments,
      initialGridConfig: new GridConfig() as Partial<GridConfig> | undefined,
      isDocumentSelectionLimitReached: false,
      isCreditCardPaymentExceededMessageShown: false,
    };
  },
  computed: {
    account(): AccountModel {
      return this.selectedAccount;
    },
    canSelectAll(): boolean {
      return this.paymentsEnabled() && this.isAccountingDocumentsSelectionOpen;
    },
    isAccountingDocumentsSelectionOpen(): boolean {
      return this.selectedAccountingDocumentType === AccountingDocumentsViewOption.Open;
    },
    isPayButtonDisabled(): boolean {
      return !this.canGoToCheckout() || this.isDocumentSelectionLimitReached;
    },
    isPayButtonVisible(): boolean {
      return this.paymentsEnabled() && this.isAccountingDocumentsSelectionOpen;
    },
  },
  watch: {
    accountId(newVal) {
      if (newVal) {
        this.getAccountBalance(newVal);
        this.fetchAccountingDocuments();
        this.resetAccountingDocumentsStore();
      }
    },
    userLocale(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.gridApi?.refreshHeader();
          this.gridApi?.redrawRows();
        });
      }
    },
    redirectResult: {
      handler(newVal) {
        if (newVal) {
          this.check3DSPayment();
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    this.initialGridConfig = this.getGridByName(this.gridName);

    // Wait for the DOM (and AG Grid component) to be ready before initializing data
    await this.$nextTick();

    await Promise.all([
      this.fetchAccountingDocuments(),
      this.check3DSPayment(),
      this.getAccountBalance(this.accountId),
    ]);

    this.gridOptions.onPaginationChanged = this.onPaginationChanged;
    this.gridOptions.onFilterChanged = this.gridFilterEventHandler;
    this.gridOptions.onSortChanged = this.onSortChanged;
  },
  beforeUnmount() {
    this.gridApi = undefined;
    this.resetAccountingDocumentsStore();
  },
  methods: {
    ...utilsMixins,
    ...accountMixins,
    ...ordersMixins,
    ...paymentsMixins,
    paymentsEnabled() {
      return this.account && this.accountCanPayOnline(this.account);
    },
    handleAccountingDocumentViewToggle(option: string) {
      this.selectedAccountingDocumentType = option as AccountingDocumentsViewOption;
      this.loadDocumentsGrid();
    },
    async fetchAccountingDocuments() {
      this.loading = true;
      this.totalRows = '';

      const validations = await this.accountingDocumentsService.getAll(this.accountId as string);

      if (validations.model) {
        if (this.paymentsEnabled()) {
          validations.model = validations.model.map((accountingDocument) => {
            accountingDocument.paidDate = accountingDocument.transactionDate ?? accountingDocument.paidDate;
            accountingDocument.totalPrice = accountingDocument.totalDocumentAmount;

            return accountingDocument;
          });
        }

        this.setAccountingDocuments(validations.model);
      } else {
        // TODO: show error
        return;
      }

      this.loadDocumentsGrid();

      this.loading = false;
    },
    async check3DSPayment() {
      if (this.show3DSProcessing || !this.redirectResult) {
        return;
      }

      this.show3DSProcessing = true;

      if (!this.paymentData) {
        this.$snotify.error(this.$t('documentsCheckout.threeds_payment_failed'));
        this.show3DSProcessing = false;

        return;
      }

      const response = await this.paymentsService.checkoutDetails(this.accountId as string, {
        redirectResult: this.redirectResult,
        paymentId: this.orderRef,
        paymentData: this.paymentData,
      });

      if (response.model.errors.length) {
        this.$snotify.error(
          this.$t(response?.model?.errors[0]?.error) || this.$t('documentsCheckout.threeds_payment_failed'),
        );
        this.fetchAccountingDocuments();
      } else {
        this.$snotify.success(this.$t('documentsCheckout.submit_payment_success'));
      }

      this.$router.push('accounting');
      this.show3DSProcessing = false;
    },
    onPaginationChanged(gridOptions: { api?: GridApi }) {
      if (gridOptions.api) {
        this.totalRows = `( ${gridOptions.api.paginationGetRowCount() ?? ''} )`;
        this.addEditGridConfig({
          name: this.gridName,
          pageNumber: gridOptions.api.paginationGetCurrentPage(),
        });
      }
    },
    loadDocumentsGrid() {
      const paymentEnabled = this.paymentsEnabled();
      const columns = [];
      this.rowData = [];

      if (this.canSelectAll) {
        const checkBoxDefinition = {
          headerCheckboxSelection: this.canSelectAll,
          headerCheckboxSelectionFilteredOnly: this.canSelectAll,
          filter: false,
          minWidth: 35,
          width: 35,
          cellClass: 'accounting-checkbox-cell',
          checkboxSelection: (params: CheckboxSelectionCallbackParams) => {
            return this.isRowSelectable(paymentEnabled, params.node);
          },
        };
        columns.push(checkBoxDefinition);
      }

      columns.push(
        {
          headerValueGetter: () => this.$t('viewAccounting.columns.document_number'),
          field: 'documentNumber',
          width: 230,
        },
        {
          headerValueGetter: () => this.$t('viewAccounting.columns.status'),
          field: 'status',
          cellRenderer: 'InvoiceStatusRenderer',
          sort: 'desc' as const,
          comparator: (statusA: string, statusB: string) => {
            const possibleStatus = ['Overdue', 'Soon', 'Open', 'In Progress', 'Settled'];
            if (possibleStatus.indexOf(statusA) !== possibleStatus.indexOf(statusB)) {
              return possibleStatus.indexOf(statusB) - possibleStatus.indexOf(statusA);
            }

            return 0;
          },
        },
        {
          field: 'referenceId',
          hide: true,
        },
        {
          headerValueGetter: () => this.$t('viewAccounting.columns.download'),
          field: 'documentDownload',
          cellRenderer: this.documentDownloadRenderer,
          cellClass: ['clickable-cell', 'justified-center'],
          width: 10,
        },
        this.dateColumnDefinition({
          headerValueGetter: () => this.$t('viewAccounting.columns.paid_date'),
          field: 'paidDate',
        }),
        this.dateColumnDefinition({
          headerValueGetter: () => this.$t('viewAccounting.columns.document_date'),
          field: 'documentDate',
        }),
        this.dateColumnDefinition({
          headerValueGetter: () => this.$t('viewAccounting.columns.due_date'),
          field: 'dueDate',
          sort: 'asc',
        }),
        {
          headerValueGetter: () => this.$t('viewAccounting.columns.order_number'),
          field: 'salesOrderNumber',
        },
        {
          headerValueGetter: () => this.$t('viewAccounting.columns.customer_purchase_order_number'),
          field: 'customerPurchaseOrderNumber',
        },
        {
          headerValueGetter: () => this.$t('viewAccounting.columns.total_price'),
          field: 'totalPrice',
          valueFormatter: this.currencyFormatter,
        },
        {
          headerValueGetter: () => this.$t('viewAccounting.columns.outstanding_price'),
          field: 'outstandingValue',
          valueFormatter: this.currencyFormatter,
        },
        /* This one to remain invisible until further notice,
            as per discussion in Demo on Oct 19th:

        { headerValueGetter: () => this.$t('viewAccounting.columns.total_if_paid_today'),
          field: 'totalPriceIfPaidToday',
          valueFormatter: this.currencyFormatter,
          width: 120 }, */
      );

      this.columnDefinitions = columns;

      const accountingDocuments = this.isAccountingDocumentsSelectionOpen ? this.openDocuments : this.closedDocuments;

      accountingDocuments.forEach((doc) => {
        if (doc.dueDate) {
          doc.dueDate = new Date(doc.dueDate);
        }

        if (doc.paidDate) {
          doc.paidDate = new Date(doc.paidDate);
        }

        if (doc.documentDate) {
          doc.documentDate = new Date(doc.documentDate);
        }

        if (typeof doc.salesOrderNumber !== 'string') {
          doc.salesOrderNumber = '';
        }

        if (typeof doc.customerPurchaseOrderNumber !== 'string') {
          doc.customerPurchaseOrderNumber = '';
        }

        if (typeof doc.documentNumber !== 'string') {
          doc.documentNumber = '';
        }

        this.rowData.push({ ...doc, visible: true });
      });

      this.gridOptions.isRowSelectable = (rowNode: any) => {
        return this.isRowSelectable(paymentEnabled, rowNode);
      };

      this.$nextTick(() => {
        this.gridApi?.setGridOption('rowData', this.rowData);
        this.restoreSelectedRows();
        this.gridApi?.sizeColumnsToFit();
      });
    },
    isRowSelectable(paymentEnabled: boolean, rowNode: any) {
      if (!paymentEnabled) {
        return false;
      }

      return this.documentCanBePayed(rowNode.data as AccountingDocument);
    },
    documentDownloadRenderer(params: any) {
      return `<button class="button download-button ${params.data.loading ? 'submitting' : ''}"></button>`;
    },
    loadingDownloadRenderer() {
      return '<button class="button download-button loading"></button>';
    },
    currencyFormatter(params: any) {
      return this.formatCurrency(params.data.currencyCode, params.value);
    },
    onGridReady(params: any) {
      this.gridApi = params.api;
      this.gridApi?.sizeColumnsToFit();
      params.api.addEventListener('cellClicked', this.onCellClick);

      if (this.initialGridConfig) {
        this.applyGridConfig(this.initialGridConfig, this.gridApi as GridApi);
      }
    },
    onSelectionChanged(event: any) {
      // This event is triggered when the selected document status is changed
      if (event.source === 'rowDataChanged') {
        return;
      }

      this.onRowSelected(event);
    },
    onRowSelected(event: any) {
      const rows = event.api.getSelectedNodes();
      let currencyCode = '';

      this.checkDocumentSelectionLimit(rows.length);

      const selectedDocuments: AccountingDocument[] = rows
        .map((row: { data: AccountingDocument }) => row.data)
        .filter((document: AccountingDocument) => this.documentCanBePayed(document));

      this.selectedDocumentIds = selectedDocuments.map((document: AccountingDocument) => {
        currencyCode = document.currencyCode;

        return document.referenceId;
      });

      this.accountDocumentCurrency = currencyCode;

      this.selectedDocumentsTotal = this.calculateTotalAmount(selectedDocuments);
      if (this.isCreditCardPaymentEnabled) {
        this.checkCreditCardPaymentLimit(this.selectedDocumentsTotal);
      }

      this.$nextTick(() => this.gridApi?.redrawRows());
    },
    checkDocumentSelectionLimit(selectedRowsLength: number) {
      if (selectedRowsLength > DOCUMENT_SELECTION_LIMIT) {
        if (!this.isDocumentSelectionLimitReached) {
          this.$toast.show({
            message: this.$t('viewAccounting.document_selection_limit_reached'),
            duration: 5000,
          });
        }
        this.isDocumentSelectionLimitReached = true;
      } else {
        this.isDocumentSelectionLimitReached = false;
      }
    },
    checkCreditCardPaymentLimit(selectedDocumentsTotal: number) {
      if (this.isCreditCardPaymentExceeded(selectedDocumentsTotal)) {
        if (!this.isCreditCardPaymentExceededMessageShown) {
          this.$toast.show({
            message: this.$t('viewAccounting.credit_card_limit_exceeded'),
            duration: 5000,
          });
        }
        this.isCreditCardPaymentExceededMessageShown = true;
      } else {
        this.isCreditCardPaymentExceededMessageShown = false;
      }
    },
    restoreSelectedRows() {
      const nodesToSelect: any[] = [];

      this.gridApi?.forEachNode((node: any) => {
        if (this.selectedDocumentIds.includes(node.data.referenceId)) {
          nodesToSelect.push(node);
        }
      });

      this.gridApi?.setNodesSelected({ nodes: nodesToSelect, newValue: true });
    },
    async onCellClick(params: CellClickedEvent) {
      if (params.colDef.field === 'documentDownload') {
        params.data.loading = true;
        params.node.setData(params.data);
        this.$nextTick(() => this.gridApi?.redrawRows());

        const validation = await this.orderDocumentsService.get(
          params.data.salesOrderNumber,
          params.data.documentNumber,
          this.accountId as string,
          this.getDocumentTypeByDocumentNumberForPaymentDocuments(params.data.documentNumber),
        );

        if (validation.isValid() && validation.model?.base64Body) {
          this.openPDF(validation.model.base64Body, params.data.documentNumber, params.data.salesOrderNumber);
        } else {
          this.$snotify.warning(this.$t('documentsCheckout.document_is_not_available'));
        }

        params.data.loading = false;
        params.node.setData(params.data);
        this.$nextTick(() => this.gridApi?.redrawRows());
      }
    },
    removeAllGridFilters() {
      this.gridApi?.setFilterModel(null);
    },
    canGoToCheckout() {
      return this.selectedDocumentIds.length && this.selectedDocumentsTotal > 0;
    },
    goToCheckout() {
      this.$router.push({
        name: RouteNames.DocumentsCheckout,
        params: {
          documentReferenceIds: this.selectedDocumentIds.join(','),
        },
      });
    },
    documentCanBePayed(document: AccountingDocument) {
      return (
        (this.accountDocumentCurrency === '' || document.currencyCode === this.accountDocumentCurrency) &&
        // the document is in a "payable status"
        document.status &&
        ![AccountingDocumentStatus.Settled, AccountingDocumentStatus.InProgress].includes(document.status)
      );
    },
    onFirstDataRendered(params: any) {
      params.api.sizeColumnsToFit();
    },
    gridFilterEventHandler(params: { api?: GridApi }) {
      const currentFilterModel = params.api?.getFilterModel();

      if (currentFilterModel) {
        this.addEditGridConfig({ name: this.gridName, filters: currentFilterModel });
      }
    },
    onSortChanged() {
      const sortedColumn = this.gridApi?.getColumnState();

      if (sortedColumn) {
        this.addEditGridConfig({ name: this.gridName, sorting: sortedColumn });
      }
    },
  },
});
</script>

<style scoped lang="scss">
.view-accounting:deep() {
  @include common-container;

  .accounting-title {
    font-weight: 400;
    text-align: left;
    font-size: 1.25rem;
    margin-bottom: 2.375rem;
    margin-top: 1rem;
  }

  .green-button {
    color: var(--primary-light-color);
  }

  .account-grid-cnt {
    @include container-background;
    margin-bottom: 40px;
  }

  .buttons {
    display: flex;
    justify-content: space-between;
    background-color: var(--primary-bg-color);
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 10px 1rem;

    .action-buttons {
      display: flex;
      gap: 10px;
    }
  }

  .as-centered-text {
    display: inline;
    text-align: center;
  }

  .ag-theme-balham .ag-cell-wrapper > *:not(.ag-cell-value):not(.ag-group-value).ag-invisible {
    display: none;
  }
}
</style>
